import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Archive } from 'lucide-react';
import { ADMIN_SECTIONS, matchesCardSearch } from '../../../config/adminNavCards';
import { useAdminNav } from '../../../contexts/AdminNavContext';

/** CRM pages bind Ctrl/⌘K to their own search palette. */
const ownsCmdK = (p: string) => p === '/crm' || p.startsWith('/crm/') || p === '/crm-v2' || p.startsWith('/crm-v2/');

const SECTION_TITLE = Object.fromEntries(ADMIN_SECTIONS.map((s) => [s.id, s.title]));

/**
 * Ctrl/⌘K quick-jump over EVERY card the user may open — including the
 * Rarely Used ones, so nothing is ever more than a few keystrokes away.
 */
export const AdminCommandPalette: React.FC = () => {
  const { enabled, cards, rarelyUsed, paletteOpen, setPaletteOpen } = useAdminNav();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !ownsCmdK(window.location.pathname)) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, setPaletteOpen]);

  useEffect(() => {
    if (paletteOpen) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [paletteOpen]);

  useEffect(() => setPaletteOpen(false), [pathname, setPaletteOpen]);

  // Escape closes the palette wherever focus is.
  useEffect(() => {
    if (!paletteOpen) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPaletteOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paletteOpen, setPaletteOpen]);

  const rarelyIds = useMemo(() => new Set(rarelyUsed.map((c) => c.id)), [rarelyUsed]);
  const results = useMemo(() => cards.filter((c) => matchesCardSearch(c, query)).slice(0, 50), [cards, query]);

  if (!enabled || !paletteOpen) return null;

  const go = (path: string) => {
    setPaletteOpen(false);
    navigate(path);
  };

  return (
    <div className="fixed inset-0 flex items-start justify-center bg-black/30 px-4 pt-[12vh]" style={{ zIndex: 10050 }} onClick={() => setPaletteOpen(false)}>
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-neutral-200 px-3">
          <Search className="h-4 w-4 text-neutral-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === 'Enter' && results[active]) {
                go(results[active].path);
              } else if (e.key === 'Escape') {
                setPaletteOpen(false);
              }
            }}
            placeholder="Jump to… e.g. Buyers List, Secrets, Bot Builder"
            className="w-full py-3 text-sm focus:outline-none"
          />
          <kbd className="rounded border border-neutral-200 px-1 text-[10px] text-neutral-400">Esc</kbd>
        </div>
        <ul className="max-h-[60vh] overflow-y-auto py-1">
          {results.length === 0 && <li className="px-4 py-6 text-center text-sm text-neutral-500">No tools match “{query}”.</li>}
          {results.map((c, i) => {
            const Icon = c.icon;
            return (
              <li key={c.id}>
                <button
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(c.path)}
                  className={`flex w-full items-center gap-3 px-3 py-2 text-left ${i === active ? 'bg-primary/10' : ''}`}
                >
                  <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br ${c.color}`}>
                    <Icon className="h-4 w-4 text-white" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-neutral-900">{c.title}</span>
                    <span className="block truncate text-xs text-neutral-500">{c.description}</span>
                  </span>
                  <span className="flex flex-shrink-0 items-center gap-1 text-[11px] text-neutral-400">
                    {rarelyIds.has(c.id) && <Archive className="h-3 w-3" />}
                    {SECTION_TITLE[c.section]}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default AdminCommandPalette;
